import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Box, Badge, Tooltip, VisuallyHidden } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { isMatchAll, filterNameMap, MATCH_FILTER_NAMES } from 'shared-components';
import { Filter } from 'types';

interface IActiveFilterProps {
  filter: Filter;
  values: string[];
  onClick?: (filter: Filter) => void;
  refreshRowsCallback?: () => void;
}

export const ActiveFilter = ({ filter, values, onClick, refreshRowsCallback }: IActiveFilterProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const clearFilter = () => {
    const params = new URLSearchParams(location.search);
    if (filter.options.NUMBER_RANGE) {
      for (const range of filter.options.NUMBER_RANGE) {
        params.delete(range.key);
      }
    }
    if (filter.key) {
      params.delete(filter.key);
      const filterName = filterNameMap[filter.key];
      if (filterName === MATCH_FILTER_NAMES.TAGS_MATCH || filterName === MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH) {
        params.delete(filterName);
      }
    }
    params.set('page', '1');
    navigate({ search: params.toString() });

    if (refreshRowsCallback) refreshRowsCallback();
  };

  const filterMultiple = values.length > 1 ? values.length : null;

  const showAll = isMatchAll(filter.key) && filterMultiple;
  const filterCount = showAll ? `All ${filterMultiple}` : filterMultiple;

  return (
    <Box ml="2" data-selector={`active-filter-${filter?.key}`}>
      <Tooltip
        hasArrow
        label={
          <>
            {showAll ? `All ${filterMultiple}:` : null}

            {values.map(value => (
              <Box whiteSpace={'break-spaces'} key={`${filter.key ?? 'Unknown key'}-${value}`}>
                {value}
              </Box>
            ))}
          </>
        }
      >
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          fontWeight="normal"
          onClick={() => onClick && onClick(filter)}
          data-selector="enroly-button"
          gap="1"
          px="2"
          maxWidth="10rem"
          flexShrink="0"
          role="button"
        >
          <Box overflow="hidden" textOverflow="ellipsis">
            {filter?.name ?? ''}
          </Box>

          {filterMultiple ? (
            <Badge variant="pill" fontSize="xs" data-selector={`filter-multiple-${filter.key}`}>
              {filterCount}
            </Badge>
          ) : null}

          <Box
            data-selector={`filter-close-icon-${filter?.key}`}
            role="button"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              event.preventDefault();
              if (event.isPropagationStopped && event.defaultPrevented) {
                clearFilter();
              }
            }}
          >
            <FontAwesomeIcon className="utility-icon" icon={faXmark} />

            <VisuallyHidden>Remove filter</VisuallyHidden>
          </Box>
        </Button>
      </Tooltip>
    </Box>
  );
};
